import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { startSignup, sendConfirmationCode, verifyConfirmationCode } from '../auth/authApi';
import { checkDatabaseUserPhoneExists } from '../api/database-userAPI';
import { validateForm } from '../utils/useValidator';
import SignupWizard from './SignupWizard';
import { Icon } from '@iconify/react';
import { countries } from '../utils/countries';
import { registerErrorHandler, showToastError, showToastSuccess } from '../utils/errorController';

const normalizePhoneNumber = (value) => String(value || '').replace(/\D/g, '');

const buildNormalizedPhone = (countryCode, phone) => `${countryCode}${normalizePhoneNumber(phone)}`;

const CodeInputs = ({ phone, countryCode, onVerified }) => {
  const [values, setValues] = useState(['', '', '', '', '', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const inputs = useRef([]);
  const lastAutoSubmittedCodeRef = useRef('');
  const submittingRef = useRef(false);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    setValues((current) => {
      const newVals = [...current];
      newVals[index] = value;
      return newVals;
    });

    if (value && index < 5) inputs.current[index + 1].focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key !== 'Backspace') return;

    e.preventDefault();

    if (values[index]) {
      setValues((current) => {
        const nextValues = [...current];
        nextValues[index] = '';
        return nextValues;
      });
      return;
    }

    if (index > 0) {
      setValues((current) => {
        const nextValues = [...current];
        nextValues[index - 1] = '';
        return nextValues;
      });
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(paste)) {
      setValues(paste.split(''));
    }
    e.preventDefault();
  };

  useEffect(() => {
    if (!toastMsg) {
      return undefined;
    }

    const timer = window.setTimeout(() => setToastMsg(''), 4000);
    return () => window.clearTimeout(timer);
  }, [toastMsg]);

  const submitCode = async (code, { auto = false } = {}) => {
    if (!/^\d{6}$/.test(code)) return;
    if (submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const res = await verifyConfirmationCode({
        phone: buildNormalizedPhone(countryCode, phone),
        code: Number(code),
      });
      if (res.success) onVerified();
      else setToastMsg('Invalid code');
    } catch (_error) {
      setToastMsg('Invalid or expired code');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }

    if (auto) {
      lastAutoSubmittedCodeRef.current = code;
    }
  };

  useEffect(() => {
    const code = values.join('');

    if (!/^\d{6}$/.test(code)) {
      lastAutoSubmittedCodeRef.current = '';
      return;
    }

    if (lastAutoSubmittedCodeRef.current === code) {
      return;
    }

    void submitCode(code, { auto: true });
  }, [values]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitCode(values.join(''));
  };

  const isComplete = values.every((value) => /^\d$/.test(value));

  return (
    <Form onSubmit={handleSubmit} className="d-flex flex-column align-items-center gap-2">
      {toastMsg && (
        <div
          className="toast-container position-fixed top-0 start-50 translate-middle-x p-3"
          style={{ zIndex: 3000 }}
        >
          <div className="toast show text-bg-danger border-0" role="alert" aria-live="assertive" aria-atomic="true">
            <div className="d-flex align-items-center justify-content-between">
              <div className="toast-body mb-0">{toastMsg}</div>
              <button
                type="button"
                className="btn-close btn-close-white ms-3 me-2"
                aria-label="Close"
                onClick={() => setToastMsg('')}
              />
            </div>
          </div>
        </div>
      )}
      <div onPaste={handlePaste} className="d-flex gap-2">
        {values.map((v, i) => (
          <Form.Control
            key={i}
            type="text"
            maxLength={1}
            ref={(el) => (inputs.current[i] = el)}
            value={v}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className="text-center text-otp-code"
            style={{ minHeight: '7rem' }}
          />
        ))}
      </div>
      <Button type="submit" className="mt-3" disabled={!isComplete || isSubmitting}>
        {isSubmitting ? 'Verifying...' : 'Verify'}
      </Button>
    </Form>
  );
};

const SignupModal = ({ show }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [wizardStep, setWizardStep] = useState('form');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState(() => {
    const au = countries.find((country) => country.country === 'AU');
    return au ? au.code : countries[0].code;
  });
  const [accept, setAccept] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ message: '', type: '' });
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isEditingPhoneInCodeStep, setIsEditingPhoneInCodeStep] = useState(false);
  const [codeStepCountryCode, setCodeStepCountryCode] = useState(countryCode);
  const [codeStepPhone, setCodeStepPhone] = useState(phone);
  const [codeStepErrors, setCodeStepErrors] = useState({});
  const RESEND_COOLDOWN_SECONDS = 120;

  useEffect(() => {
    registerErrorHandler(setToast);

    return () => registerErrorHandler(() => { });
  }, []);

  useEffect(() => {
    if (!toast.message) {
      return undefined;
    }

    const timer = window.setTimeout(
      () => setToast({ message: '', type: '' }),
      toast.duration || 5000,
    );
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (step !== 'code') {
      setResendCountdown(0);
      setIsEditingPhoneInCodeStep(false);
      setCodeStepCountryCode(countryCode);
      setCodeStepPhone(phone);
      setCodeStepErrors({});
      return undefined;
    }

    setResendCountdown(RESEND_COOLDOWN_SECONDS);
  }, [step]);

  useEffect(() => {
    if (step !== 'code' || resendCountdown <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setResendCountdown((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [step, resendCountdown]);

  const handleSend = async (e) => {
    e.preventDefault();
    const fields = [
      { field: 'email', label: 'Email', required: true, type: 'email' },
      { field: 'phone', label: 'Phone Number', required: true },
    ];

    const errs = validateForm(fields, { email, phone });
    const normalizedPhone = normalizePhoneNumber(phone);

    if (normalizedPhone.length <= 7) {
      errs.phone = 'This is not a Phone number format';
    }

    if (!accept) errs.accept = 'You must agree before continuing';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      const phoneExistsResponse = await checkDatabaseUserPhoneExists(
        normalizedPhone,
        countryCode,
      );

      if (phoneExistsResponse?.exists) {
        showToastError('This mobile number is already linked to an existing account.');
        return;
      }

      await startSignup({
        phone: buildNormalizedPhone(countryCode, phone),
        email,
      });
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      setStep('code');
      showToastSuccess('Verification code sent via SMS');
    } catch (_error) {
      showToastError('Unable to send verification code via SMS');
    }
  };

  const handleComplete = () => {
    navigate('/sign-in');
  };

  const handleUpdateCodeStepPhone = async () => {
    const fields = [{ field: 'phone', label: 'Phone Number', required: true }];
    const errs = validateForm(fields, { phone: codeStepPhone });
    const normalizedPhone = normalizePhoneNumber(codeStepPhone);
    if (normalizedPhone.length <= 7) {
      errs.phone = 'This is not a Phone number format';
    }
    setCodeStepErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      const phoneExistsResponse = await checkDatabaseUserPhoneExists(
        normalizedPhone,
        codeStepCountryCode,
      );

      if (phoneExistsResponse?.exists) {
        showToastError('This mobile number is already linked to an existing account.');
        return;
      }

      await startSignup({
        phone: buildNormalizedPhone(codeStepCountryCode, codeStepPhone),
        email,
      });
      setPhone(codeStepPhone);
      setCountryCode(codeStepCountryCode);
      setCodeStepErrors({});
      setIsEditingPhoneInCodeStep(false);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      showToastSuccess('Verification code sent to your updated number');
    } catch (_error) {
      showToastError('Unable to update phone number');
    }
  };

  const toastType = toast.type || 'danger';
  const toastStyleMap = {
    success: {
      container: 'bg-success-100',
      text: 'text-success',
    },
    warning: {
      container: 'bg-warning-100',
      text: 'text-warning',
    },
    danger: {
      container: 'bg-danger-100',
      text: 'text-danger',
    },
  };
  const activeToastStyle = toastStyleMap[toastType] || toastStyleMap.danger;

  return (
    <>
      {show && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content p-24" style={{ margin: '4rem' }}>
              <div className="modal-header border-0 px-32">
                <h4 className="text-center text-samurai-dark w-100 fw-bolder mb-0">
                  {step === 'form'
                    ? "Let's Get to Work"
                    : step === 'code'
                      ? 'Verify Your Phone'
                      : wizardStep === 'setup'
                        ? 'Setting Up Your System'
                        : wizardStep === 'complete'
                          ? 'Login to Begin Your Maintenance Journey'
                          : wizardStep === 'organisation'
                            ? 'Tell Us About Your Company'
                            : 'Tell Us About You'}
                </h4>
              </div>

              <div className="modal-body px-32">
                {toast.message && (
                  <div
                    className="toast-container position-fixed top-0 start-50 translate-middle-x p-3"
                    style={{ zIndex: 3000 }}
                  >
                    <div className={`toast show ${activeToastStyle.container} border-0 p-12`}>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className={`toast-body fw-semibold m-0 ${activeToastStyle.text}`}>
                          {toast.message}
                        </div>
                        <button
                          type="button"
                          className="btn-close ms-3 me-2"
                          aria-label="Close"
                          onClick={() => setToast({ message: '', type: '' })}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {step === 'form' && (
                  <><div className="text-center">
                    <h6 className="text-lg text-neutral-500 mb-3">
                      You&apos;re just minutes away from smarter, easier maintenance.</h6>
                    <p>Start with the basics and add tools like meters, measurements, and event planning when you're ready.</p>
                    <div className="d-inline-flex flex-column align-items-start gap-2 mx-auto mt-4 mb-3">
                      <div className="d-flex align-items-start gap-2 text-start">
                        <Icon
                          icon="mdi:check"
                          style={{
                            fontSize: '1.25rem',
                            color: '#198754',
                            marginTop: '2px',
                            flexShrink: 0,
                          }} />
                        <span>Trusted by maintenance professionals like you</span>
                      </div>
                      <div className="d-flex align-items-start gap-2 text-start">
                        <Icon
                          icon="mdi:check"
                          style={{
                            fontSize: '1.25rem',
                            color: '#198754',
                            marginTop: '2px',
                            flexShrink: 0,
                          }} />
                        <span>Easy setup. No fuss.</span>
                      </div>
                      <div className="d-flex align-items-start gap-2 text-start">
                        <Icon
                          icon="mdi:check"
                          style={{
                            fontSize: '1.25rem',
                            color: '#198754',
                            marginTop: '2px',
                            flexShrink: 0,
                          }} />
                        <span>Enter your work email and phone to begin</span>
                      </div>
                    </div>
                  </div>
                    <Form.Group className="px-32 pb-16">
                      <InputGroup>
                        <InputGroup.Text>
                          <Icon icon="mage:email" style={{ fontSize: '1.25rem' }} />
                        </InputGroup.Text>
                        <Form.Control
                          type="email"
                          placeholder="Enter your work email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={errors.email ? 'is-invalid' : ''}
                          required
                        />
                      </InputGroup>
                      {errors.email && (
                        <div className="invalid-feedback d-block px-32">
                          {errors.email}
                        </div>
                      )}
                    </Form.Group>

                    <Form.Group className="px-32 pb-16 d-flex gap-2">
                      <div>
                        <Form.Label>Country Code</Form.Label>
                        <Form.Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                          {countries.map((country) => (
                            <option key={`${country.country}-${country.code}`} value={country.code}>
                              {country.label} ({country.code})
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="flex-grow-1">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter your phone number"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className={errors.phone ? 'is-invalid' : ''}
                          required
                        />
                        {errors.phone && (
                          <div className="invalid-feedback d-block">
                            {errors.phone}
                          </div>
                        )}
                      </div>
                    </Form.Group>

                    <Form onSubmit={handleSend}>
                      <Form.Check
                        type="checkbox"
                        label={
                          <span>
                            By signing up, you accept our{' '}
                            <a
                              href="/assets/docs/eula.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-700 fw-semibold"
                            >
                              Terms of Service
                            </a>{' '}
                            and{' '}
                            <a
                              href="https://www.samurai-cmms.com/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-700 fw-semibold"
                            >
                              Privacy Policy
                            </a>
                          </span>
                        }
                        checked={accept}
                        onChange={(e) => {
                          setAccept(e.target.checked);
                          if (errors.accept) setErrors({});
                        }}
                        className={`px-32 py-16 ${errors.accept ? 'is-invalid' : ''}`}
                        required
                        style={{
                          gap: '0.5rem',
                          display: 'flex',
                          alignItems: 'center'
                        }} />
                      {errors.accept && (
                        <div className="invalid-feedback d-block px-32">
                          {errors.accept}
                        </div>
                      )}
                      <Form.Check
                        label="Get helpful tips, product updates and offers via email"
                        className="px-32 pb-16"
                        style={{
                          gap: '0.5rem',
                          display: 'flex',
                          alignItems: 'center'
                        }} />
                      <Button className="btn-lg bg-success" type="submit" style={{ marginTop: '1rem', width: '100%' }}>
                        Sign Up Now
                      </Button>
                      <div className="text-center mt-12">
                        Already have an account? <Link to="/sign-in" className="text-primary-700">Sign in</Link>
                      </div>
                    </Form></>
                )}

                {step === 'code' && (
                  <div>
                    <h6 className="text-lg text-neutral-500 mb-3">We&apos;ve sent a 6-digit code to {countryCode} {phone}.</h6>
                    <p>Enter it below to confirm your phone and keep your account secure.</p>
                    <CodeInputs
                      key={`${countryCode}-${phone}`}
                      phone={phone}
                      countryCode={countryCode}
                      onVerified={() => setStep('details')}
                    />
                    {resendCountdown === 0 ? (
                      <div className="text-center mt-3">
                        <Button
                          variant="link"
                          onClick={async () => {
                            try {
                              setResendCountdown(RESEND_COOLDOWN_SECONDS);
                              await sendConfirmationCode({
                                phone: buildNormalizedPhone(countryCode, phone),
                              });
                              showToastSuccess('Verification code resent via SMS');
                            } catch (_error) {
                              showToastError('Unable to resend verification code');
                              setResendCountdown(0);
                            }
                          }}
                          className="mt-3 text-center"
                        >
                          Resend code
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center mt-3 text-secondary">
                        Resend code available in {Math.floor(resendCountdown / 60)}:
                        {String(resendCountdown % 60).padStart(2, '0')}
                      </div>
                    )}
                    {!isEditingPhoneInCodeStep ? (
                      <div className="text-center mt-3">
                        <Button
                          variant="link"
                          onClick={() => {
                            setCodeStepCountryCode(countryCode);
                            setCodeStepPhone(phone);
                            setCodeStepErrors({});
                            setIsEditingPhoneInCodeStep(true);
                          }}
                          className="mt-2 text-center"
                        >
                          Not your number?
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-3 border rounded p-3">
                        <div className="d-flex gap-2">
                          <div>
                            <Form.Label>Country Code</Form.Label>
                            <Form.Select
                              value={codeStepCountryCode}
                              onChange={(e) => setCodeStepCountryCode(e.target.value)}
                            >
                              {countries.map((country) => (
                                <option key={`${country.country}-${country.code}`} value={country.code}>
                                  {country.label} ({country.code})
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className="flex-grow-1">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                              type="text"
                              value={codeStepPhone}
                              onChange={(e) => setCodeStepPhone(e.target.value)}
                              className={codeStepErrors.phone ? 'is-invalid' : ''}
                            />
                            {codeStepErrors.phone && (
                              <div className="invalid-feedback d-block">
                                {codeStepErrors.phone}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-3">
                          <Button type="button" onClick={handleUpdateCodeStepPhone}>
                            Update Number
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={() => {
                              setIsEditingPhoneInCodeStep(false);
                              setCodeStepErrors({});
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {step === 'details' && (
                  <SignupWizard
                    email={email}
                    verifiedPhone={normalizePhoneNumber(phone)}
                    verifiedCountryCode={countryCode}
                    onComplete={handleComplete}
                    onStepChange={setWizardStep}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>

  );
};

export default SignupModal;
